import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';

import ProductsList from '../../../component/productsList/ProductsList';
import { PRODUCT_FRAGMENT } from '../../../component/productPreview/ProductPreview';
import { PRODUCT_LIST_ITEM_FRAGMENT } from '../../../query/productFragments';
import { CATEGORY_ALL } from '../../discover/discoverConstants';

// Search filter & sort integrations
import { useFilterStore } from '../../search/searchFilterPanel/FilterStoreProvider';
import FilterPanel from '../../search/searchFilterPanel/FilterPanel';
import MobileFilterPanel from '../../search/searchFilterPanel/MobileFilterPanel';
import useProductSearchVariables from '../../search/searchProductContainer/hooks/useProductSearchVariables';
import useSortStore from '../../search/searchSort/SortStore';
import BrandFilterToolbar from './BrandFilterToolbar';
import { SORT_OPTIONS as SORT } from '../../search/searchSort/SortOptions';
import useFilterSortUrlSync from '../../../hook/useFilterSortUrlSync';
import FilterTags from '../../search/searchFilterTags/FilterTags';
import { getAllCategories } from '../../search/searchUtils';
import useMediaQuery from '../../../hook/useMediaQuery';
import { useMounted } from '../../../hook/useMounted';
import '../../search/Search.css';
import './BrandFiltersAndResults.css';

// Query for product search
const SEE_PRODUCTS = gql`
    query SeeProducts($filter: ProductFilter!, $offset: Int, $limit: Int, $sort: String, $isSearch: Boolean) {
        searchProducts(filter: $filter, offset: $offset, limit: $limit, sort: $sort, isSearch: $isSearch) {
            ...ProductFragment
            ...ProductListItemFragment
            price
        }
    }
    ${PRODUCT_FRAGMENT}
    ${PRODUCT_LIST_ITEM_FRAGMENT}
`;

// For FilterTags subcategory mapping
const GET_CATEGORIES_FOR_FILTER = gql`
    query getCategoriesCached {
        getCategoriesCached(limit: 100) {
            name
            subcategories {
                id
                name
            }
        }
    }
`;

export default function BrandFiltersAndResults({ brandName, productCount, selectedCategory, isSearchFlag }) {
    const filters = useFilterStore((state) => state.filters);
    const updateFilter = useFilterStore((state) => state.updateFilter);
    const clearAllFilters = useFilterStore((state) => state.clearAllFilters);

    const selectedSort = useSortStore((state) => state.selectedSort);
    const setSort = useSortStore((state) => state.setSort);

    const isMounted = useMounted();
    const [isFilterOpen, setIsFilterOpen] = React.useState(false);
    const [stickyOffsets, setStickyOffsets] = React.useState({});
    const isMobile = useMediaQuery('(max-width: 767px)');
    const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
    const isSmall = isMobile || isTablet;
    const toolbarRef = React.useRef(null);

    // URL <-> state sync
    useFilterSortUrlSync();

    // Categories for FilterTags (with subcategories)
    const { data: catData } = useQuery(GET_CATEGORIES_FOR_FILTER);
    const allCategories = getAllCategories(catData);

    // If URL category exists (and no explicit subcategoryIds), select all subcategories once
    const lastSyncedCategoryRef = React.useRef(null);
    const location = useLocation();

    useEffect(() => {
        // 전체 카테고리면 스킵
        if (!selectedCategory || selectedCategory === CATEGORY_ALL) return;
        
        // 무한루프 방지: 이미 동기화된 카테고리면 스킵
        if (lastSyncedCategoryRef.current === selectedCategory.name) return;

        // URL에 이미 subcategoryIds가 있으면 사용자가 직접 선택한 것이므로 덮어쓰지 않음
        const searchParams = new URLSearchParams(location.search);
        const hasUrlSubcategoryIds = !!searchParams.get('subcategoryIds');
        if (hasUrlSubcategoryIds) {
            lastSyncedCategoryRef.current = selectedCategory.name;
            return;
        }

        // 카테고리 데이터가 없으면 스킵
        const list = catData && catData.getCategoriesCached ? catData.getCategoriesCached : [];
        const cat = list.find((c) => c.name === selectedCategory.name);
        if (!cat || !cat.subcategories || cat.subcategories.length === 0) return;

        // URL 카테고리에 해당하는 모든 서브카테고리를 필터에 자동 선택
        const targetIds = cat.subcategories.map((sub) => parseInt(sub.id, 10));

        updateFilter('subcategoryIds', targetIds);
        updateFilter('categories', []);
        
        // 동기화 완료 표시 (재실행 방지)
        lastSyncedCategoryRef.current = selectedCategory.name;
    }, [selectedCategory, catData, updateFilter]);

    useEffect(() => {
        const navigationElement = document.querySelector('.Navigation-nav');
        const submenuElement = document.querySelector('.Navigation-submenu');
        const toolbarElement = toolbarRef.current;

        if (!navigationElement || !toolbarElement) {
            return undefined;
        }

        const updateStickyOffsets = () => {
            const navigationBottom = navigationElement.getBoundingClientRect().bottom;
            const submenuHeight =
                submenuElement && window.getComputedStyle(submenuElement).display !== 'none'
                    ? submenuElement.getBoundingClientRect().height
                    : 0;
            const headerOffset = navigationBottom + submenuHeight;
            const toolbarHeight = toolbarElement.getBoundingClientRect().height;
            const seamOverlap = 1;

            setStickyOffsets({
                toolbarTop: Math.max(headerOffset - seamOverlap, 0),
                sidebarTop: Math.max(headerOffset + toolbarHeight - seamOverlap, 0),
            });
        };

        updateStickyOffsets();

        let resizeObserver;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(updateStickyOffsets);
            resizeObserver.observe(navigationElement);
            if (submenuElement) {
                resizeObserver.observe(submenuElement);
            }
            resizeObserver.observe(toolbarElement);
        } else {
            window.addEventListener('resize', updateStickyOffsets);
        }

        return () => {
            if (resizeObserver) {
                resizeObserver.disconnect();
            } else {
                window.removeEventListener('resize', updateStickyOffsets);
            }
        };
    }, []);

    const handleRemoveFilter = (filterType, value) => {
        switch (filterType) {
            case 'gender':
                updateFilter('gender', '');
                break;
            case 'subcategoryIds':
                updateFilter('subcategoryIds', filters.subcategoryIds.filter((id) => id !== value));
                break;
            case 'brands':
                updateFilter(
                    'brands',
                    (filters.brands || []).filter((brand) => {
                        const brandNameVal = typeof brand === 'string' ? brand : brand.name;
                        const valueName = typeof value === 'string' ? value : value.name;
                        return brandNameVal !== valueName;
                    })
                );
                break;
            case 'price':
                updateFilter('priceMin', null);
                updateFilter('priceMax', null);
                break;
            case 'isSelling':
                updateFilter('isSelling', false);
                break;
            case 'condition':
                updateFilter('condition', '');
                break;
            case 'sizeValueIds': {
                const removeIds = new Set((value?.valueIds || []).map(Number));
                const remaining = (filters.sizeValueIds || []).filter(
                    (id) => !removeIds.has(Number(id))
                );
                updateFilter('sizeValueIds', remaining);
                break;
            }
            default:
                break;
        }
    };

    const handleClearAllFilters = () => {
        clearAllFilters();
        setSort(SORT.POPULAR.code);
    };

    const baseVariables = useProductSearchVariables(undefined, filters, selectedSort);
    const variables = React.useMemo(
        () => ({
            ...baseVariables,
            filter: {
                ...baseVariables.filter,
                brand: brandName,
                category: selectedCategory === CATEGORY_ALL ? undefined : selectedCategory.name,
            },
            isSearch: isSearchFlag,
        }),
        [baseVariables, brandName, selectedCategory, isSearchFlag]
    );

    // 브랜드 페이지에서는 브랜드 필터 태그를 표시하지 않습니다.
    const filtersForTags = React.useMemo(() => ({
        ...filters,
        brands: [],
    }), [filters]);

    return (
        <>
            <BrandFilterToolbar
                productCount={productCount}
                selectedSort={selectedSort}
                onSortChange={setSort}
                onFilterOpen={() => setIsFilterOpen(true)}
                stickyOffset={stickyOffsets.toolbarTop}
                toolbarRef={toolbarRef}
            />

            <div className="BrandFiltersAndResults-content">
                {!isSmall && (
                    <div
                        className="Search-sidebar"
                        style={stickyOffsets.sidebarTop ? {'--search-sticky-top': `${stickyOffsets.sidebarTop}px`} : undefined}
                    >
                        {isMounted && <FilterPanel hideBrandFilter />}
                    </div>
                )}
                <div className={`Search-results ${isSmall ? 'Search-results-full' : ''}`}>
                    {!isSmall && (
                        <FilterTags
                            filters={filtersForTags}
                            onRemoveFilter={handleRemoveFilter}
                            onClearAll={handleClearAllFilters}
                            categories={allCategories}
                        />
                    )}
                    <ProductsList itemsProperty="searchProducts" query={SEE_PRODUCTS} variables={variables} />
                </div>
            </div>

            {isSmall && isMounted && (
                <MobileFilterPanel isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} hideBrandFilter />
            )}
        </>
    );
}

BrandFiltersAndResults.propTypes = {
    brandName: PropTypes.string.isRequired,
    productCount: PropTypes.number,
    selectedCategory: PropTypes.shape({
        name: PropTypes.string.isRequired,
    }).isRequired,
    isSearchFlag: PropTypes.bool,
};
